import { enableProdMode, importProvidersFrom, provideZoneChangeDetection } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideKeycloak } from 'keycloak-angular';

import { AppComponent } from './app/app.component';
import { AppModule } from './app/app.module';
import { environment } from './environments/environment';
import { Config } from './app/services/config/config';
import { APP_CONFIG } from './app/services/config/config.service';

const getConfigFile = (): string => {
	switch (environment.name) {
		case 'STAGING':
			return 'config.stg.json';
		case 'PROD':
			return 'config.prod.json';
		case 'LOCAL':
			return 'config.local.json';
		case 'DEV':
		default:
			return 'config.json';
	}
};

(async () => {
	try {
		const configPath = `/config/${getConfigFile()}`;

		const response = await fetch(configPath);
		if (!response.ok) {
			throw new Error(`Could not load configuration file from ${configPath}`);
		}
		const config: Config = await response.json();

		if (config.production) {
			enableProdMode();
			console.warn = () => {};
			console.error = () => {};
		}

		// AppComponent is standalone, so it must be bootstrapped via bootstrapApplication (an NgModule
		// may no longer bootstrap a standalone component). AppModule is kept as a provider/import
		// container and pulled in via importProvidersFrom; BrowserModule and BrowserAnimationsModule were
		// removed from it (bootstrapApplication provides the browser platform; animations now come from
		// provideAnimations).
		//
		// provideKeycloak creates the Keycloak instance but does NOT auto-initialize it (no initOptions):
		// the app's own APP_INITIALIZER first runs the legacy-auth-code / resident-redirect check, then
		// calls keycloak.init() explicitly. The bearer token is added by KeycloakBearerInterceptor.
		bootstrapApplication(AppComponent, {
			providers: [
				{ provide: APP_CONFIG, useValue: config },
				provideZoneChangeDetection(),
				provideAnimations(),
				provideKeycloak({
					config: {
						url: config.keycloakUrl,
						realm: config.keycloakRealm,
						clientId: config.keycloakClient,
					},
				}),
				importProvidersFrom(AppModule),
			],
		}).catch((err) => console.error(err));
	} catch (error) {
		console.error('Critical error during application bootstrap:', error);
		document.body.innerHTML = '<h1>Error initializing application. Please contact support.</h1>';
	}
})();
